import type { QueryView } from '@krp/client-runtime'

/**
 * Структура базы 1С (ONEC-STRUCT): организации и структурные единицы со
 * связями. Ключи — Ref_Key ЭТОЙ базы (rid проверен декодером). «Не задано в
 * 1С» (`empty`) и «такой связи в конфигурации нет» (`unsupported`) — разные
 * ответы и показываются разными словами.
 */
export type StructureLinkState = 'linked' | 'empty' | 'unresolved' | 'unsupported'

export interface StructureLink {
  state: StructureLinkState
  /** Ключ для `linked` и `unresolved`, иначе `null`. */
  key: string | null
}

export interface StructureOrganization {
  key: string
  name: string
  code: string | null
}

export type StructureUnitKind = 'department' | 'warehouse' | 'other'

export interface StructureUnit {
  key: string
  name: string
  code: string | null
  kind: StructureUnitKind
  /** Тип структурной единицы 1С дословно; `null` — в конфигурации типа нет. */
  nativeType: string | null
  organization: StructureLink
  /** Ближайшее подразделение вверх по иерархии. */
  department: StructureLink
  parent: StructureLink
  hasRetailPriceType: boolean
}

export interface StructureRelations {
  unitOrganization: boolean
  unitHierarchy: boolean
  departments: boolean
  unitDepartment: boolean
}

export interface OneCStructure {
  organizations: StructureOrganization[]
  units: StructureUnit[]
  relations: StructureRelations
  /** Когда служба прочитала структуру из 1С (живое чтение или её память не старше минуты). */
  observedAt: string
}

/** Склад базы для выпадающих списков разделов: имя, организация и подразделение словами. */
export interface StructureWarehouse {
  refKey: string
  name: string
  organizationName: string | null
  departmentName: string | null
  hasRetailPriceType: boolean
}

export interface WarehouseOption {
  value: string
  label: string
}

/** Группа списка: организация · подразделение (или «без …» словами). */
export interface WarehouseOptionGroup {
  label: string
  options: readonly WarehouseOption[]
}

/**
 * Общий выбор склада базы: один для поставок, инвентаризации и ценников.
 * `options` — плоский список (подпись «склад · организация»), `groups` — он же,
 * разложенный по организации и подразделению. Склады — только из ответа 1С:
 * при отказе 1С списка нет (ни прежнего, ни пустого), `query` несёт её текст.
 */
export interface WarehousePickVm {
  query: QueryView
  warehouses: readonly StructureWarehouse[]
  options: readonly WarehouseOption[]
  groups: readonly WarehouseOptionGroup[]
  ready: boolean
  /** Ревизия настроек базы, по которой служба ответила: её ждут команды разделов. */
  configurationVersion: string
}

// --- экран «Структура 1С» ---------------------------------------------------------------------

/** Слайс `structure` (срок `screen`): что человек искал и раскрыл в структуре каждой базы. */
export interface StructureViewState {
  search: string
  /** Свёрнутые узлы дерева (ключи). */
  collapsed: string[]
  /** Выбранный узел — его карточка справа. */
  selected: string | null
}

export type StructureIntentState = Record<string, StructureViewState>

export const EMPTY_VIEW: StructureViewState = { search: '', collapsed: [], selected: null }

export type StructureNodeKind = 'organization' | 'department' | 'warehouse' | 'other' | 'group'

export interface StructureNodeVm {
  /** Ключ узла (ключ 1С или служебный ключ группы «без организации»). */
  id: string
  kind: StructureNodeKind
  name: string
  /** Тип 1С или пояснение: «Склад», «Подразделение», «склады без подразделения». */
  caption: string | null
  depth: number
  expandable: boolean
  expanded: boolean
  selected: boolean
  /** Сколько складов внутри узла (для организации и подразделения). */
  warehouses: number
}

export interface StructureFactVm {
  label: string
  value: string
  /** Связь не задана, оборвана или её нет в конфигурации — показывается значком и словами. */
  tone: 'ok' | 'missing' | 'unsupported'
}

export interface StructureDetailsVm {
  id: string
  kind: StructureNodeKind
  name: string
  facts: readonly StructureFactVm[]
}

export interface StructureScreenVm {
  resourceId: string
  query: QueryView
  search: string
  nodes: readonly StructureNodeVm[]
  details: StructureDetailsVm | null
  /** Сводка: «2 организации, 3 подразделения, 7 складов». */
  summary: string | null
  /** «Прочитано из 1С в 14:32» — только при показанной структуре; при отказе 1С — `null`. */
  readAt: string | null
  /** Связи, которых в конфигурации этой базы нет, — словами. */
  limits: readonly string[]
  refreshing: boolean
  /**
   * Что повторяет «Повторить» отказа: `read` — чтение структуры, `refresh` —
   * «Обновить из 1С» (отказ пришёл на неё, и прежнюю структуру экран уже не показывает).
   */
  retry: 'read' | 'refresh'
}

export interface StructureScreenActions {
  searchChanged: (text: string) => void
  toggled: (id: string) => void
  selected: (id: string) => void
  refresh: () => void
  retry: () => void
}
